import Modal from './smartlock.modal.component';
import template from './smartlockPanel.component.html';


/* @ngInject */
function SmartlockPanel(
  $scope,
  growl,
  $uibModal,
  uiGridConstants,
  Smartlock,
  DashboardService,
  ModalService,
) {

  /**
   * Private Variables
   */
  const vm = this;
  let gridAPI;
  const listeners = [];

  /**
   * Public Variables
   */
  vm.loadingSpinners = {};
  vm.editPanel = {
    needsToBeConfigured: false
  };
  vm.equipmentFilterText = {};
  vm.equipmentModel = {};

  /**
   * Public Methods
   */
  vm.$onInit = init;
  vm.$onDestroy = destroyPanel;
  vm.openRequestCodeModal = openRequestCodeModal;
  vm.revokeCode = revokeCode;

  function init() {
    vm.currentDashboard = DashboardService.getCurrentDashboard();
    vm.panel = vm.currentDashboard.panels[vm.panelId];
    if (_.isEmpty(vm.panel.equipment)) {
      // panel needs to be configured
      vm.loadingSpinners.stopPanel = true;
      vm.editPanel.needsToBeConfigured = true;
    } else {
      // panel is good to go
      vm.loadingSpinners.stopPanel = false;
      vm.editPanel.needsToBeConfigured = false;
      vm.equipmentGrid = configureGrid();
      populateGrid();
      listeners.push(...setupListeners());
    }

  }

  function openRequestCodeModal(row) {
    vm.selectedEquipmentModel = row?.entity;
    $uibModal
      .open({
        ...Modal,
        size: 'md',
        resolve: {
          lockModel() {
            return vm.selectedEquipmentModel;
          }
        }
      })
      .result
      .then(requestCode);
  }

  function requestCode(code) {
    return Smartlock
      .requestCode(code, vm.selectedEquipmentModel)
      .then((newCode) => {
        configureGridData(vm.selectedEquipmentModel);
        gridAPI.core.notifyDataChange(uiGridConstants.dataChange.ALL);
        growl.success(`Successfully assigned code ${newCode} for ${code.name}`, { ttl: -1 });
      })
      .catch((err) => {
        growl.error(err.data, { title: 'Failed to request code:' });
      });
  }

  /**
   * @typedef {object} Lease
   * A QC-code lease
   * Up to 100 of these may be issued per padlock, ever*
   * @property {object} name The assigned lease owner
   * @property {string} phone Contact #
   * @property {string} code The code bits
   * @property {object} [prettyCode] Formatted code equivalent
   * @property {boolean} assigned Lease has owner? (How is this different from infferring a lease by name? -Bin)
   * @property {string} [assignedDate] Date
   * @property {string} type single|multi
   * @property {boolean} pending Transaction lock
   * @property {boolean} revoked The code is permanently unusable
   */

  /**
   * @param {Lease} lease To be revoked
   * @param {string} padlock ID of padlock
   * @returns {undefined}
   */
  function revokeCode(lease, padlock) {
    const { code, name } = lease;
    ModalService.confirmationModal({
      buttonText: 'Revoke',
      danger: true,
      onConfirm: async () => {
        try {
          const newModel = await Smartlock.revokeCode(code, padlock, vm.panelId);
          gridAPI.grid.modifyRows(newModel);
          gridAPI.core.refresh();
          growl.success(`${name}'s code has been revoked`);
        } catch (err) {
          console.error(err);
          growl.error(err.data || err, { title: 'Unable to revoke code' });
        }
      }
    });
  }

  function configureGrid() {
    const toColDef = (field, name, priority) => ({
      field,
      name,
      cellTooltip: true,
      type: 'string',
      enableHiding: false,
      sort: {
        direction: uiGridConstants.ASC,
        priority
      },
    });

    return {
      appScopeProvider: vm,
      minRowsToShow: 3,
      enableFiltering: true,
      enableSelectAll: false,
      multiSelect: false,
      enableHorizontalScrollbar: uiGridConstants.scrollbars.NEVER,
      enableVerticalScrollbar: uiGridConstants.scrollbars.WHEN_NEEDED,
      expandableRowTemplate: `
        <div
          ui-grid="row.entity.subGridOptions"
          class="grid"
          ui-grid-auto-resize
          ng-style="{height: (row.entity.formattedQCs.codes.length * row.entity.subGridOptions.rowHeight) + 39 + 'px'}"
        ></div>
      `,
      showExpandAllButton: false,
      enableOnDblClickExpand: false,
      onRegisterApi,
      columnDefs: [
        toColDef('eq.name', 'Name', 0),
        toColDef('eq.settings.serial', 'Serial', 1),
        {
          name: 'Add Leaseholder',
          cellTemplate: `
            <div
              class="ui-grid-cell-contents bg-success text-success button-cell"
              ng-click="grid.appScope.openRequestCodeModal(row, $event)"
              title="Create new code for {{row.entity.eq.name}}"
            >
              <i class="far fa-plus flat"></i>
            </div>
          `,
          headerCellTemplate: '<span></span>',
          enableColumnResizing: false,
          width: '45',
          pinnedRight: true,
        }]
    };
  }

  function configureGridData(data) {
    _.forEach(_.castArray(data), (eqModel) => {
      const codes = eqModel.formattedQCs.codes;
      eqModel.subGridOptions = {
        columnDefs: [
          { name: 'Leaseholder', field: 'name' },
          { name: 'Code', field: 'prettyCode' },
          { field: 'type', width: '7%' },
          { field: 'phone' },
          {
            name: 'Issued',
            field: 'assignedDate',
            type: 'date',
            cellFilter: "date: 'medium'",
            sort: { direction: uiGridConstants.DESC }
          },
          {
            name: 'Revoke',
            cellTemplate: `
              <div
                class="ui-grid-cell-contents bg-danger text-danger button-cell"
                ng-click="grid.appScope.revokeCode(row.entity, '${eqModel.eq.id}')"
                title="Revoke {{row.entity.name}}'s code"
              >
                <i class="fas fa-times flat"></i>
              </div>
            `,
            enableColumnResizing: false,
            width: '45',
            pinnedRight: true,
          },
        ],
        data: codes,
        disableRowExpandable: _.isEmpty(codes),
        appScopeProvider: vm,
        enableVerticalScrollbar: uiGridConstants.scrollbars.NEVER,
      };
    });

    return { data };
  }

  function populateGrid() {
    return Smartlock
      .getEquipmentFromIds(vm.panel.id, vm.panel.equipment, vm.equipmentGrid)
      .then((equipmentModel) => {
        if (_.isEmpty(equipmentModel)) {
          vm.editPanel.needsToBeConfigured = true;
          return;
        }
        vm.equipmentModel = equipmentModel;
        _.merge(vm.equipmentGrid, configureGridData(vm.equipmentModel));
      })
      .catch(err => growl.error(err.error || err, { title: 'Failed to retrieve panel - Refresh and try again' }))
      .finally(() => {
        vm.loadingSpinners.stopPanel = true;
      });
  }

  function setupListeners() {
    return [

      /*
        Run in (API) debug session to emulate this event:
        global.sails.sockets.broadcast(
          'equipment-5b4666faa841692c0067a973',
          'Event:created',
          { eventName: 'padlocks/quickClickAssigned', equipment:"5b4666faa841692c0067a973" }
        );
        */
      $scope.$on('event:padlocks/quickClickAssigned', (__, { equipment }) => {
        if (!vm.panel.equipment.includes(equipment.id)) {
          return;
        }
        // Need to regenerate QC cache
        populateGrid();
        gridAPI.core.refresh();
      }),

    ];
  }

  function onRegisterApi(gridApi) {
    gridAPI = gridApi;
    // dynamic expandable rows
    gridApi.expandable.on.rowExpandedBeforeStateChanged($scope, (row) => {
      row.expandedRowHeight = 30 + (row.entity.formattedQCs.codes.length * 30);

    });
  }

  function destroyPanel() {
    listeners.forEach(unsub => unsub());
    Smartlock.deleteModel(vm.panel.id);
  }
}


export default {
  name: 'smartlockPanel',
  template,
  controller: SmartlockPanel,
  bindings: {
    panelId: '<'
  }
};
